import React, { FC } from 'react';
import { leftPadSamples, resample, rightPadSamples, removeDCBias } from '../../utils/util';
import { MS_PER_COLUMN, MV_PER_ROW, ECG_PLOT_WIDTH } from 'utils/constants';
import { ColorValueHex } from 'types/types';

// ECG grid scaling
// https://www.medscape.com/answers/1894014-178289/what-are-the-elements-of-the-electrocardiography-ecg-grid

const makeGridPath = (
  width: number,
  height: number,
  spacingX: number,
  spacingY: number,
  offsetX: number,
  offsetY: number,
) => {
  let path = '';

  // Draw the horizontal lines
  for (let y = offsetY; y < height + offsetY; y += spacingY) {
    path += `M 0 ${y} h ${width}`;
  }

  // Draw the vertical lines
  for (let x = offsetX; x < width + offsetX; x += spacingX) {
    path += `M ${x} 0 v ${height}`;
  }

  return path;
};

const getPointsToPlot = (
  samples: number[],
  sampleRate: number,
  startTime: number,
  timeWidth: number,
  pixelWidth: number,
) => {
  const startSampleIndex = startTime * sampleRate;
  const sampleCountToShow = Math.floor(sampleRate * timeWidth);
  const samplesToPlotUnpadded = samples.slice(startSampleIndex, startSampleIndex + sampleCountToShow);

  const totalDuration = samples.length / sampleRate;

  const samplesToPlot =
    startTime < 0
      ? leftPadSamples(samples, sampleCountToShow)
      : startTime + timeWidth > totalDuration
      ? rightPadSamples(samples, sampleCountToShow)
      : samplesToPlotUnpadded;

  return resample(samplesToPlot, pixelWidth, sampleRate);
};

const secondsToPixels = (seconds: number, pixelWidth: number, timeWidth: number) => pixelWidth * (seconds / timeWidth);

const mvToPixels = (mv: number, pixelHeight: number, mvHeight: number) => pixelHeight * (mv / (mvHeight * 500));

const plotPoints = (points: number[], plotWidth: number, plotHeight: number, mvHeight: number) => {
  const returnArray = points.reduce((path, sample, i) => {
    const x = (i / points.length) * plotWidth;
    const y = plotHeight / 2 - mvToPixels(sample, plotHeight, mvHeight);

    const xStr = x.toFixed(2);
    const yStr = y.toFixed(2);

    return path + `L ${xStr} ${yStr}`;
  }, `M 0 ${plotHeight / 2}`);

  return returnArray;
};

const mmToDp = (mm: number) => {
  const dpi = 160;

  return mm * (dpi / 25.4);
};

const renderECGGrid = (
  width: number,
  height: number,
  squareWidth: number,
  squareHeight: number,
  offsetX: number,
  offsetY: number,
) => {
  const smallGridPath = makeGridPath(width + offsetX, height + offsetY, squareWidth, squareHeight, -offsetX, -offsetY);

  const bigGridPath = makeGridPath(
    width + offsetX,
    height + offsetY,
    squareWidth * 5,
    squareHeight * 5,
    -offsetX,
    -offsetY,
  );

  return {
    smallGridPath,
    bigGridPath,
  };
};

const gridSquareSizeBySize = (mmSize: number) => ({
  width: mmToDp(mmSize),
  height: mmToDp(mmSize),
});

const gridSquareSizeByScale = (pixelWidth: number, pixelHeight: number, timeWidth: number, millivolts: number) => {
  const numColumns = (timeWidth * 1000) / MS_PER_COLUMN;
  const numRows = (2 * millivolts) / MV_PER_ROW;

  return {
    width: pixelWidth / numColumns,
    height: pixelHeight / numRows,
  };
};

type ECGPlotProps = {
  samples: number[];
  sampleRate: number;
  startTime: number;
  physicalGridSquareSize?: number;
  durationToShow?: number;
  millivolts?: number;
  full?: boolean;
  showGrid?: boolean;
  signalStrokeWidth?: number;
  smallGridStrokeWidth?: number;
  bigGridStrokeWidth?: number;
  strokeColor?: ColorValueHex;
  drawChart?: number;
  decreaseWave?: number;
  live?: boolean;
  plotHeight?: number;
  plotWidth?: number;
  cellColor?: ColorValueHex;
  backgroundFill?: ColorValueHex;
};

export const ECGPlot: FC<ECGPlotProps> = ({
  samples,
  sampleRate = 512,
  startTime,
  physicalGridSquareSize,
  durationToShow,
  millivolts,
  full,
  showGrid = true,
  signalStrokeWidth = 1.5,
  smallGridStrokeWidth,
  bigGridStrokeWidth,
  strokeColor,
  decreaseWave = 200,
  plotHeight = 250,
  plotWidth = ECG_PLOT_WIDTH,
  cellColor = '#FF0000',
  backgroundFill = '#000000',
}) => {
  if (physicalGridSquareSize !== undefined && (durationToShow !== undefined || millivolts !== undefined)) {
    throw new Error('Must define either physical size or duration / mv scale. Not both.');
  }

  const screenWidth = window.innerWidth;
  const width = plotWidth ?? screenWidth - 100;
  const height = plotHeight;
  const columnAdjustment = 2;
  if (physicalGridSquareSize !== undefined) {
    const columnCount = width / mmToDp(physicalGridSquareSize);
    durationToShow = (MS_PER_COLUMN * columnAdjustment * columnCount) / 1000;

    const rowCount = height / mmToDp(physicalGridSquareSize) + decreaseWave;
    millivolts = MV_PER_ROW * rowCount;
  }

  const recordingDuration = samples.length / sampleRate;
  const unBiasedSamples = removeDCBias(samples);
  const timeWidth = full ? recordingDuration : durationToShow;

  // How many pixels wide is the plot?
  const pixelWidth = full ? (screenWidth * timeWidth) / durationToShow : width;
  const pixelHeight = height;

  if (startTime === undefined) {
    startTime = recordingDuration - timeWidth;
  }

  const pointsToPlot = getPointsToPlot(
    unBiasedSamples,
    sampleRate,
    startTime, // Plot the last section by default
    timeWidth,
    pixelWidth,
  );

  const plotPath = pixelHeight === 0 ? '' : plotPoints(pointsToPlot, pixelWidth, pixelHeight, 2 * millivolts);

  // Grids

  const { width: gridSquareWidth, height: gridSquareHeight } =
    physicalGridSquareSize !== undefined
      ? gridSquareSizeBySize(physicalGridSquareSize)
      : gridSquareSizeByScale(pixelWidth, pixelHeight, timeWidth, millivolts);

  const offsetX = secondsToPixels(startTime % ((5 * MS_PER_COLUMN) / 1000), pixelWidth, timeWidth);

  const offsetY = 5 * gridSquareHeight - ((pixelHeight / 2) % (5 * gridSquareHeight));
  const { smallGridPath, bigGridPath } =
    pixelHeight === 0
      ? { smallGridPath: '', bigGridPath: '' }
      : renderECGGrid(pixelWidth, pixelHeight, gridSquareWidth, gridSquareHeight, offsetX, offsetY);

  return (
    <svg width={width} height={height}>
      {showGrid && (
        <g>
          <rect width='100%' height='100%' fill={backgroundFill} />
          <path d={smallGridPath} stroke={cellColor} strokeWidth={smallGridStrokeWidth ?? 0.21} />
          <path d={bigGridPath} stroke={cellColor} strokeWidth={bigGridStrokeWidth ?? 0.4} />
        </g>
      )}
      <path
        d={plotPath}
        stroke={strokeColor}
        strokeWidth={signalStrokeWidth || 3}
        strokeLinejoin='round'
        strokeMiterlimit={1}
        fill='none'
      />
    </svg>
  );
};
